import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { mediaUrl } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { ArrowLeft } from "lucide-react";

function renderMarkdown(md) {
  const blocks = md.split(/\n\n+/);
  const out = [];
  blocks.forEach((block, bi) => {
    const trimmed = block.trim();
    if (trimmed.startsWith("### ")) {
      out.push(<h3 key={bi}>{trimmed.slice(4)}</h3>);
      return;
    }
    const lines = trimmed.split("\n");
    const isOl = lines.every((l) => /^\d+\.\s/.test(l));
    const isUl = lines.every((l) => /^[-*]\s/.test(l));
    if (isOl) {
      out.push(<ol key={bi}>{lines.map((l, li) => <li key={li} dangerouslySetInnerHTML={{ __html: inline(l.replace(/^\d+\.\s/, "")) }} />)}</ol>);
      return;
    }
    if (isUl) {
      out.push(<ul key={bi}>{lines.map((l, li) => <li key={li} dangerouslySetInnerHTML={{ __html: inline(l.replace(/^[-*]\s/, "")) }} />)}</ul>);
      return;
    }
    out.push(<p key={bi} dangerouslySetInnerHTML={{ __html: inline(trimmed) }} />);
  });
  return out;
}

function inline(text) {
  return text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
}

export default function BlogPost() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}?lang=${lang}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug, lang]);

  useEffect(() => {
    if (post?.title) document.title = `${post.title} · ${t("title.suffix")}`;
    else if (notFound) document.title = t("title.blog");
  }, [post, notFound, t]);

  if (notFound) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      <h1 className="font-serif font-bold text-3xl text-slate-900">{t("blog.notFound")}</h1>
      <Link to="/blog" className="mt-4 inline-block text-teal-700 hover:underline">{t("blog.backToBlog")}</Link>
    </div>
  );

  if (!post) return <div className="min-h-[60vh] grid place-items-center text-slate-400 font-mono text-sm">{t("blog.loading")}</div>;

  return (
    <article className="max-w-3xl mx-auto px-6 py-12" data-testid="blog-post">
      <Link to="/blog" className="flex w-fit items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8">
        <ArrowLeft size={16} /> {t("blog.allArticles")}
      </Link>
      <span className="font-mono text-[11px] uppercase tracking-widest text-teal-700">{post.category}</span>
      <h1 className="mt-3 font-serif font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-[1.1]">{post.title}</h1>
      <div className="mt-4 flex items-center gap-3 text-sm text-slate-400 font-mono">
        <span>{post.author}</span><span>·</span>
        <span>{new Date(post.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>
      </div>
      {post.cover_image && (
        <div className="mt-8 rounded-2xl overflow-hidden aspect-[16/9] bg-slate-100">
          <img src={mediaUrl(post.cover_image)} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}
      {(() => {
        const content = (post.content || "").replace(/\u00a0/g, " ");
        const isHtml = /^\s*<(p|h[1-6]|ul|ol|blockquote|div|img|strong|em|a|br)/i.test(content);
        return isHtml ? (
          <div lang={lang} className="prose-nlp mt-10 text-lg" dangerouslySetInnerHTML={{ __html: content }} />
        ) : (
          <div lang={lang} className="prose-nlp mt-10 text-lg">{renderMarkdown(content)}</div>
        );
      })()}
    </article>
  );
}
